import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Tooltip,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  DirectionsCarFilled,
} from '@mui/icons-material';
import { t } from 'i18next';
import Logo from '../../../../assets/images/logo/vms-pro-logo.svg';

const formatAddress = (obj) => [obj?.street, obj?.city, obj?.country].filter(Boolean).join(', ');
const vehicleDescription = (v) =>
  [v?.vehicleType?.name || v?.vehicleType, v?.year, v?.make?.name || v?.make, v?.model?.name || v?.model]
    .filter(Boolean)
    .join(' ');
const formatCurrency = (val) =>
  val || val === 0
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val)
    : '-';

const ContainerPrintPreview = ({ open, onClose, row, containerIndex }) => {
  const printRef = useRef(null);
  if (!row || containerIndex == null) return null;
  const booking = row.booking || {};
  const container = row.containers?.[containerIndex];
  if (!container) return null;
  const shipmentMode = row?.shipmentMode || row?.type;
  const shipmentItems = container?.shipmentItems || [];
  const exporter = row?.exporter || row?.client?.exporter;
  const firstShipmentItem = shipmentItems[0];
  const consignee = firstShipmentItem?.consignee || row?.consignee;
  const notifyParty = firstShipmentItem?.notifyParty || row?.notifyParty;
  const consigneeDock = firstShipmentItem?.consigneeDock || row?.consigneeDock;
  const vehicles = shipmentItems.length
    ? shipmentItems.map((item) => item?.vehicle).filter(Boolean)
    : container?.vehicles || [];
  const containerPriceTotal = vehicles.reduce((sv, v) => sv + (Number(v.price ?? v.unitPrice) || 0), 0);
  const containerHasPrice = containerPriceTotal > 0;
  const bookingDestination = booking?.destination?.name || booking?.destination;
  const primaryClient = {
    id: row?.primaryClientId,
    name: row?.primaryClientName,
    company: row?.primaryClientCompany,
  };

  const handlePrint = () => {
    const printNode = printRef.current;
    if (!printNode) return;

    const printFrame = document.createElement('iframe');
    const appStyles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => node.outerHTML)
      .join('\n');

    printFrame.setAttribute('title', 'container-print-preview');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';

    printFrame.onload = () => {
      const frameWindow = printFrame.contentWindow;
      const frameDocument = printFrame.contentDocument;
      if (!frameWindow || !frameDocument) return;

      const images = Array.from(frameDocument.images || []);
      const waitForImages = images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });

      Promise.all(waitForImages).then(() => {
        frameWindow.focus();
        frameWindow.print();
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 500);
      });
    };

    printFrame.srcdoc = `
      <!doctype html>
      <html dir="${document.documentElement.dir || 'ltr'}">
        <head>
          <meta charset="utf-8" />
          <title>${t('Container Preview')} #${containerIndex + 1}</title>
          ${appStyles}
          <style>
            @page { size: auto; margin: 12mm; }
            * { box-sizing: border-box; }
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
              color: #1f2933;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            body { font-family: Roboto, Arial, sans-serif; }
            .print-document { width: 100%; }
            .no-print { display: none !important; }
            .print-area {
              position: static !important;
              inset: auto !important;
              padding: 0 !important;
              background: #fff !important;
            }
            .table-header {
              background: #e5e7eb !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .section-title {
              background: #0B3C5D !important;
              color: #fff !important;
              padding: 4px 8px;
              border-radius: 4px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .vehicle-card-row {
              display: block !important;
              overflow: visible !important;
              padding: 0 !important;
            }
            .vehicle-card-row > * {
              width: 100% !important;
              max-width: 100% !important;
              break-inside: avoid;
              page-break-inside: avoid;
              margin-bottom: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              break-inside: auto;
            }
            tr {
              break-inside: avoid;
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          <div class="print-document">${printNode.outerHTML}</div>
        </body>
      </html>
    `;

    document.body.appendChild(printFrame);
  };
  const handleCopy = (text) => {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };
  const getItemClient = (item) => ({
    id: item?.client?.id || item?.vehicle?.clientId || primaryClient?.id,
    name: item?.client?.name || item?.vehicle?.clientName || primaryClient?.name,
    company: item?.client?.company || item?.vehicle?.clientCompany || primaryClient?.company,
  });
  const vehicleColor = (v) => v?.color?.name || v?.color;
  const vehicleDestination = (v) => bookingDestination || container?.destination || v?.destination?.name || v?.destination;
  const getBillOfLadingLabel = (files) => {
    if (!files?.length) return '-';
    return files.map((file) => file?.fileName || file?.name || file?.originalFileName).filter(Boolean).join(', ') || t('Attached');
  };
  const clipText = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    maxWidth: '100%',
  };
  const renderPartyBlock = (title, party, numberLabel = t('Reference #')) => {
    if (!party) return null;
    const address = formatAddress(party);

    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0B3C5D' }}>{title}</Typography>
        <Typography variant="body2" sx={clipText} title={party.name || ''}>{party.name}</Typography>
        <Typography variant="caption" display="block" sx={clipText} title={party.consigneeNumber || party.exportNumber || party.notifyPartyNumber || party.consigneeDockNumber || ''}>
          <strong>{numberLabel}:</strong> {party.consigneeNumber || party.exportNumber || party.notifyPartyNumber || party.consigneeDockNumber || '-'}
        </Typography>
        {party.phoneNumber && <Typography variant="caption" display="block" sx={clipText} title={party.phoneNumber}>{t('Phone')}: {party.phoneNumber}</Typography>}
        {party.email && <Typography variant="caption" display="block" sx={clipText} title={party.email}>{t('Email')}: {party.email}</Typography>}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ...clipText }} title={address}>{address}</Typography>
      </Box>
    );
  };
  const renderVehicleStat = (label, value, copyValue) => (
    <Box sx={{ minWidth: 0, p: 1, border: '1px solid #eef2f5', borderRadius: 1.5, bgcolor: '#fff' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap title={value || '-'}>
          {value ?? '-'}
        </Typography>
        {copyValue && (
          <Tooltip title={t('Copy VIN')} arrow>
            <IconButton size="small" onClick={() => handleCopy(copyValue)} sx={{ p: 0.25 }} className="no-print">
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
  const renderVehicleDetailsCard = (item, itemIndex) => {
    const vehicle = item?.vehicle || item;
    if (!vehicle) return null;
    const itemClient = getItemClient(item);
    const desc = vehicleDescription(vehicle) || t('Vehicle');
    const vin = vehicle?.vin;

    return (
      <Box
        key={item?.id || vehicle?.id || itemIndex}
        sx={{
          flex: { xs: '0 0 88vw', md: '0 0 560px', lg: '0 0 620px' },
          width: { xs: '88vw', md: 560, lg: 620 },
          p: 1.5,
          border: '1px solid #dbe7f3',
          borderRadius: 2,
          backgroundColor: '#fbfdff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#E1F0FF', color: '#0B3C5D', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
            <DirectionsCarFilled fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0B3C5D' }} noWrap title={desc}>
              {t('Vehicle')} #{itemIndex + 1}: {desc}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {itemClient?.name ? `${t('Client')}: ${itemClient.name}${itemClient.company ? ` (${itemClient.company})` : ''}` : `${t('Client')}: -`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1, mb: 1.5 }}>
          {renderVehicleStat(t('VIN'), vin, vin)}
          {renderVehicleStat(t('Client'), itemClient?.name ? `${itemClient.name}${itemClient.company ? ` (${itemClient.company})` : ''}` : '-')}
          {renderVehicleStat(t('Weight'), vehicle?.weight)}
          {renderVehicleStat(t('Fuel'), vehicle?.fuelType?.name || vehicle?.fuelType)}
          {renderVehicleStat(t('Color'), vehicleColor(vehicle))}
          {renderVehicleStat(t('Destination'), vehicleDestination(vehicle))}
          {renderVehicleStat(t('Price'), vehicle?.price ?? vehicle?.unitPrice)}
          {renderVehicleStat(t('Description'), item?.description || row?.primaryDescription || '-')}
          {renderVehicleStat(t('Bill Of Lading'), getBillOfLadingLabel(item?.billOfLadingFile))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, pt: 1.5, borderTop: '1px solid #eef2f5' }}>
          {renderPartyBlock(t('Consignee'), item?.consignee, t('Consignee #')) || (
            <Typography variant="body2" color="text.secondary">{t('Consignee')}: -</Typography>
          )}
          {renderPartyBlock(t('Notify Party'), item?.notifyParty, t('Notify Party #')) || (
            <Typography variant="body2" color="text.secondary">{t('Notify Party')}: -</Typography>
          )}
          {renderPartyBlock(t('Consignee Dock'), item?.consigneeDock, t('Consignee Dock #')) || (
            <Typography variant="body2" color="text.secondary">{t('Consignee Dock')}: -</Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 600 }}>{t('Container Preview')} #{containerIndex + 1}</DialogTitle>
  <DialogContent dividers sx={{ bgcolor: '#FAFBFC' }}>
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .print-area, .print-area * { visibility: visible; }
            .print-area { position: absolute; inset: 0; padding: 0 !important; }
            .no-print { display: none !important; }
            .table-header { background: #e5e7eb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .section-title { background:#0B3C5D !important; color:#fff !important; padding:4px 8px; border-radius:4px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .vehicle-card-row { overflow: visible !important; display: block !important; }
            .vehicle-card-row > * { width: 100% !important; break-inside: avoid; margin-bottom: 12px; }
          }
        `}</style>
        <Box ref={printRef} className="print-area" sx={{ p: 2 }}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, pb:1, borderBottom:'2px solid #0B3C5D' }}>
            <Box component="img" src={Logo} alt="VMS PRO" sx={{ height:46, width:'auto', display:'block' }} />
            <Typography variant="caption" sx={{ fontStyle:'italic', color:'text.secondary' }}>Powered by Pinetechs.com</Typography>
          </Box>
          <Typography variant="h6" className="section-title" sx={{ mb: 2, display: 'inline-block' }}>{t('Container Summary')}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {container.containerNumber && <Chip size="small" label={`${t('Number')}: ${container.containerNumber}`} />}
            {container.sealNumber && <Chip size="small" label={`${t('Seal')}: ${container.sealNumber}`} />}
            {container.containerSize && <Chip size="small" label={`${t('Size')}: ${container.containerSize}`} />}
            <Chip size="small" color="info" label={`${t('Net Weight')}: ${container.netWeight ?? 0}`} />
            <Chip size="small" label={`${t('Parts')}: ${container.partsInContainer ? t('Yes') : t('No')}`} />
            {container?.client?.name && <Chip size="small" label={`${t('Client')}: ${container.client.name}`} />}
            {containerHasPrice && <Chip size="small" color="warning" label={`${t('Value')}: ${formatCurrency(containerPriceTotal)}`} />}
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t('Booking')}</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
            <Typography variant="caption"><strong>{t('Booking Number')}:</strong> {booking.number || '-'}</Typography>
            <Typography variant="caption"><strong>{t('Status')}:</strong> {booking?.bookingStatus?.name || '-'}</Typography>
            <Typography variant="caption"><strong>{t('Destination')}:</strong> {booking?.destination?.name || '-'}</Typography>
            <Typography variant="caption"><strong>{t('Vessel')}:</strong> {booking.vesselName || '-'}</Typography>
            <Typography variant="caption"><strong>{t('Voyage')}:</strong> {booking.voyageNumber || '-'}</Typography>
            <Typography variant="caption"><strong>{t('Entry Date')}:</strong> {row.entryDate || '-'}</Typography>
          </Stack>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, mb: 2 }}>
            {renderPartyBlock(t('Exporter'), exporter, t('Export #'))}
            {shipmentMode === 'FCL' && renderPartyBlock(t('Consignee'), consignee, t('Consignee #'))}
            {shipmentMode === 'FCL' && renderPartyBlock(t('Notify Party'), notifyParty, t('Notify Party #'))}
            {shipmentMode === 'FCL' && renderPartyBlock(t('Consignee Dock'), consigneeDock, t('Consignee Dock #'))}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{t('Vehicles')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('Total')}: {vehicles.length}</Typography>
            </Box>
          </Box>
          {vehicles.length > 0 && ['LCL', 'SPL'].includes(shipmentMode) && (
            <Box
              className="vehicle-card-row"
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 1.5,
                mb: 2,
                overflowX: 'auto',
                overflowY: 'hidden',
                pb: 1,
                pr: 0.5,
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#c8d0d8',
                  borderRadius: 8,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#eef2f5',
                  borderRadius: 8,
                },
              }}
            >
              {(shipmentItems.length ? shipmentItems : vehicles).map((item, itemIndex) => renderVehicleDetailsCard(item, itemIndex))}
            </Box>
          )}
          {vehicles.length > 0 && !['LCL', 'SPL'].includes(shipmentMode) && (
            <Table size="small" sx={{ mb: 2, '& th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow className="table-header">
                  <TableCell>{t('ID')}</TableCell>
                  <TableCell>{t('Description')}</TableCell>
                  <TableCell>{t('Client')}</TableCell>
                  <TableCell>{t('VIN')}</TableCell>
                  <TableCell>{t('Destination')}</TableCell>
                  <TableCell>{t('Weight')}</TableCell>
                  <TableCell>{t('Fuel Type')}</TableCell>
                  {containerHasPrice && <TableCell>{t('Price')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {(shipmentItems.length ? shipmentItems : vehicles).map((item, vi) => {
                  const v = item?.vehicle || item;
                  const itemClient = getItemClient(item);
                  const price = Number(v.price ?? v.unitPrice) || 0;
                  return (
                    <TableRow key={v.id || vi}>
                      <TableCell>{v.id}</TableCell>
                      <TableCell>{vehicleDescription(v)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={itemClient?.name || '-'}>
                          {itemClient?.name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{v.vin}</TableCell>
                      <TableCell>{vehicleDestination(v) || '-'}</TableCell>
                      <TableCell>{v.weight}</TableCell>
                      <TableCell>{v.fuelType?.name || v.fuelType}</TableCell>
                      {containerHasPrice && <TableCell>{price ? formatCurrency(price) : '-'}</TableCell>}
                    </TableRow>
                  );
                })}
                {containerHasPrice && (
                  <TableRow>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 600 }}>{t('Total')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(containerPriceTotal)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <Divider />
          <Box sx={{ mt:4, pt:1, borderTop:'1px dashed #b0bec5', textAlign:'center' }} className="no-print print-brand-footer">
            <Typography variant="caption" sx={{ color:'text.secondary' }}>VMS PRO • Powered by Pinetechs.com</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="no-print" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">{t('Vehicles')}: {vehicles.length} {containerHasPrice && `| ${t('Value')}: ${formatCurrency(containerPriceTotal)}`}</Typography>
        <Box>
          <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>{t('Close')}</Button>
          <Button onClick={handlePrint} variant="contained">{t('Print')}</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

ContainerPrintPreview.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
  containerIndex: PropTypes.number,
};

export default ContainerPrintPreview;
